import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAsyncData } from "@/hooks/useAsyncData";
import { listAll, removeItem, updateItem } from "@/lib/adminRepository";
import { ADMIN } from "@/constants/testIds";
import { Plus, Pencil, Trash2, Trophy } from "lucide-react";

const dateKey = (x) => x.awardDate || x.year || "";
const byDateDesc = (a, b) =>
  String(dateKey(b)).localeCompare(String(dateKey(a))) || (a.order ?? 99) - (b.order ?? 99);

export default function AwardsList() {
  const { data: rows, loading, error, reload } = useAsyncData(() => listAll("awards"), []);
  const items = [...(rows || [])].sort(byDateDesc);

  const del = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete award "${item.title || item.id}"?`)) return;
    try {
      await removeItem("awards", item.id);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    await updateItem("awards", item.id, { status: next });
    reload();
  };

  return (
    <div data-testid="admin-awards">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow text-bronze mb-1">Content</div>
          <h1 className="font-serif text-3xl text-ink">Awards</h1>
          <p className="text-sm text-olive mt-1">Industry recognition — image-first masonry on /awards, newest first by award date.</p>
        </div>
        <Link to="/admin/awards/new" data-testid={ADMIN.newButton} className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} /> New award
        </Link>
      </div>

      {loading && <p className="text-olive">Loading…</p>}
      {error && <p className="text-red-700">Failed to load. <button className="underline" onClick={reload}>Retry</button></p>}

      {!loading && !error && (
        <table className="w-full text-sm border border-bronze/20 bg-cream" data-testid={ADMIN.list}>
          <thead className="bg-beige/60 text-left text-[0.65rem] uppercase tracking-wider text-olive">
            <tr>
              <th className="p-3 w-20">Image</th>
              <th className="p-3">Award</th>
              <th className="p-3 w-32">Award date</th>
              <th className="p-3 w-24">Visibility</th>
              <th className="p-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-bronze/10 align-middle" data-testid={`admin-row-${item.id}`}>
                <td className="p-2">
                  <div className="h-12 w-14 overflow-hidden bg-beige border border-bronze/20 flex items-center justify-center">
                    {item.image ? <img src={item.image} alt="" className="h-full w-full object-cover" /> : <Trophy size={16} className="text-bronze/50" />}
                  </div>
                </td>
                <td className="p-3">
                  <div className="font-medium text-ink">{item.title || item.id}</div>
                  <div className="text-xs text-olive">{item.org || item.body || "—"}</div>
                </td>
                <td className="p-3 text-xs text-ink_soft" data-testid={`award-date-${item.id}`}>{item.awardDate || item.year || "—"}</td>
                <td className="p-3">
                  <button
                    onClick={() => togglePublish(item)}
                    data-testid={ADMIN.publishToggle}
                    className={`px-2 py-1 text-[0.6rem] uppercase tracking-wider ${item.status === "published" ? "bg-bronze text-cream" : "bg-taupe/40 text-ink"}`}
                  >
                    {item.status || "draft"}
                  </button>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/awards/${item.id}`} className="text-bronze hover:text-bronze_dark" title="Edit"><Pencil size={15} /></Link>
                    <button onClick={() => del(item)} data-testid={ADMIN.deleteButton} className="text-red-600 hover:text-red-800" title="Delete"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-olive">No awards yet — click “New award”.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
